import React, { Suspense, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { debounce } from '@amzn/storm-ui-utils';
import {
  SIDEBAR_TYPE,
  SideNavLinkItemType,
} from '@amzn/aac-chrome-internal-api';
import { useConsolidatedNavContext } from 'src/providers/ConsolidatedNavContextProvider';
import {
  Overlay,
  ToolbarWrapper,
  ToolbarOutterWrapper,
  AllToolsHiddenContent,
  NavItemsContainer,
} from 'src/features/nav/components/ConsolidatedSideNav/styles';
import SideNavLink from 'src/features/nav/components/ConsolidatedSideNav/SideNavLink';
import Accordion from 'src/features/nav/components/ConsolidatedSideNav/Accordion/Accordion';
import SideNavModal from './SideNavModal';
import SideNavButton from './SideNavButton';
import AllToolsNavItem from './AllToolsComponents/AllToolsNavItem';
import SideNavAllToolsButton from './AllToolsComponents/SideNavAllToolsButton';
import {
  CC_MODAL_BULLSEYE_TOOLBAR_ID,
  WeblabedFeatures,
} from 'src/commons/constants';
import { logWithTakt } from 'src/commons/utils/logWithTakt';
import { isA3cChatDisabled } from 'src/commons/utils/a3cChatGate';
import { useDomRefById, useIsFeatureEnabled } from 'src/commons/hooks';
import { ErrorBoundary } from 'src/commons/components/ErrorBoundary';
import { InterstitialErrorBoundary } from './InterstitialModal/InterstitialErrorBoundary';
import AdsAgentNavButton from './AdsAgentNavButton';
import { useChromeConfigContext } from 'src/providers/ChromeConfigContextProvider';
import { useInternalChromeConfigContext } from 'src/providers/InternalChromeConfigContextProvider';
import { loadInterstitialModalRemote } from './InterstitialModal/loadInterstitialRemote';

const InterstitialModal = React.lazy(
  () =>
    loadInterstitialModalRemote() as Promise<{
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      default: React.ComponentType<any>;
    }>,
);

const OverlayContainer = () => {
  const [portalElement, setPortalElement] = useState<Element | null>(null);
  useEffect(() => {
    const portal = document.getElementById('AACChromePortal');
    if (portal && document.body.contains(portal)) {
      setPortalElement(portal);
    }
    return () => {
      setPortalElement(null);
    };
  }, []);

  if (!portalElement) {
    return null;
  }

  return createPortal(
    <Overlay data-ccx-e2e-id="chrome-overlay" data-test-id="chrome-overlay" />,
    portalElement,
  );
};

export const ConsolidatedSideNavInner: React.VFC = () => {
  const {
    isQuickNavModalEnabled,
    allToolbarItems,
    isNavOpen,
    isAllToolsOpen,
    setIsNavOpen,
    setIsAllToolsOpen,
    toolbarItems,
    showInterstitialModal,
    interstitialDestinationUrl,
    setInterstitialDestinationUrl,
    interstitialAppName,
    setInterstitialAppName,
  } = useConsolidatedNavContext();

  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [hasInteracted, setHasInteracted] = useState<boolean>(false);
  // This ref is required so that we can access the current isFocused value inside the debounce function below
  const isFocusedReference = useRef<boolean>();
  const hasLoggedNavOpen = useRef<boolean>(false);
  const hasLoggedAllToolsOpen = useRef<boolean>(false);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const portalRef = useDomRefById('AACChromePortal');
  const sidenavPopoverPortalRef = useDomRefById('AACChromeGlobalNavPortal');
  isFocusedReference.current = isFocused;
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<string>('auto');

  const allToolsEnabled = !!allToolbarItems?.length;

  const a3cConfig = useChromeConfigContext()?.a3cConfig;
  // Embedded config wins over flyover config (mirrors EmbeddedChatManager).
  const selectedAgentIdentifier =
    a3cConfig?.embeddedChatConfig?.selectedAgentIdentifier ??
    a3cConfig?.flyoverChatConfig?.selectedAgentIdentifier;
  // A3C is fully disabled on this page — via the AAO unified gate vetoing it
  // (window.isA3CEnabled === false), loadBehavior DISABLED, or the
  // chat-disabled URL list — so hide the Ads Agent nav button even when the
  // A3C_EVERYWHERE weblab or a page-provided agent would show it by default.
  const isA3cDisabled = isA3cChatDisabled(a3cConfig);
  // Mirror EmbeddedChatManager: the chat loads under either everywhere tier,
  // so the nav button must too (T1 or T2).
  const isA3cEverywhereT1Enabled = useIsFeatureEnabled(
    WeblabedFeatures.A3C_EVERYWHERE,
  );
  const isA3cEverywhereT2Enabled = useIsFeatureEnabled(
    WeblabedFeatures.A3C_EVERYWHERE_T2,
  );
  const isA3cEverywhereEnabled =
    isA3cEverywhereT1Enabled || isA3cEverywhereT2Enabled;
  const isInterstitialTrimT1Enabled = useIsFeatureEnabled(
    WeblabedFeatures.MIGRATE_INTERSTITIAL_TO_TRIM,
  );
  const isInterstitialTrimT2Enabled = useIsFeatureEnabled(
    WeblabedFeatures.MIGRATE_INTERSTITIAL_TO_TRIM_T2,
  );
  const interstitialDataMode = isInterstitialTrimT2Enabled
    ? 'trim-shadow'
    : isInterstitialTrimT1Enabled
      ? 'trim'
      : 'control';
  const shouldShowAdsAgentButton =
    !isA3cDisabled && (!!selectedAgentIdentifier || isA3cEverywhereEnabled);
  const { config } = useInternalChromeConfigContext();

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isNavOpen && isAllToolsOpen) {
      setIsAllToolsOpen(false);
    }
  }, [isNavOpen]);

  useEffect(() => {
    if (isNavOpen && !hasLoggedNavOpen.current) {
      logWithTakt('aac-chrome-global-navigation-open', null);
      hasLoggedNavOpen.current = true;
    }
  }, [isNavOpen && !hasLoggedNavOpen.current]);

  useEffect(() => {
    if (isAllToolsOpen && !hasLoggedAllToolsOpen.current) {
      logWithTakt('aac-chrome-all-tools-panel-open', null);
      hasLoggedAllToolsOpen.current = true;
    }
  }, [isAllToolsOpen]);

  useEffect(() => {
    if (containerRef.current && document.body.contains(containerRef.current)) {
      const container = containerRef.current;
      const width = container.scrollWidth;
      setContainerWidth(`${width}px`);
    }
  }, [allToolsEnabled]);

  const handleEnterNav = (e) => {
    const target = e?.target as HTMLElement;
    if (portalRef?.current?.contains(target)) {
      return;
    }
    if (sidenavPopoverPortalRef?.current?.contains(target)) {
      return;
    }
    if (!isNavOpen) {
      setIsNavOpen(true);
    }
  };

  const handleLeaveNav = () => {
    setIsNavOpen(false);
  };

  const handleClose = () => {
    setInterstitialDestinationUrl('');
    setInterstitialAppName('');
  };

  const handleEnterTools = (e) => {
    const target = e?.target as HTMLElement;
    if (portalRef?.current?.contains(target)) {
      return;
    }
    if (sidenavPopoverPortalRef?.current?.contains(target)) {
      return;
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    if (!isNavOpen) {
      setIsNavOpen(true);
    }
    if (!isAllToolsOpen) {
      setIsAllToolsOpen(true);
    }
  };

  const handleLeaveTools = (delay) => {
    if (delay) {
      hideTimeoutRef.current = setTimeout(() => {
        setIsAllToolsOpen(false);
      }, 300);
    } else {
      setIsAllToolsOpen(false);
    }
  };

  return (
    <>
      <ToolbarOutterWrapper
        isNavOpen={isNavOpen}
        isAllToolsOpen={isAllToolsOpen}
        onMouseEnter={(e) => {
          if (!hasInteracted) {
            setHasInteracted(true);
          }
          handleEnterNav(e);
        }}
        onMouseOver={handleEnterNav}
        onMouseLeave={handleLeaveNav}
        onFocus={(e) => {
          const target = e?.target as HTMLElement;
          if (portalRef?.current?.contains(target)) {
            return;
          }
          if (sidenavPopoverPortalRef?.current?.contains(target)) {
            return;
          }
          if (!hasInteracted) {
            setHasInteracted(true);
          }
          setIsFocused(true);
          !isNavOpen && setIsNavOpen(true);
        }}
        onBlur={(e) => {
          const target = e?.target as HTMLElement;
          if (portalRef?.current?.contains(target)) {
            return;
          }
          if (sidenavPopoverPortalRef?.current?.contains(target)) {
            return;
          }
          setIsFocused(false);
          debounce(
            () => !isFocusedReference.current && setIsNavOpen(false),
            100,
          )();
        }}
        data-ccx-e2e-id="chrome-side-nav"
      >
        <ToolbarWrapper
          isNavOpen={isNavOpen}
          onFocus={(e) => {
            const target = e?.target as HTMLElement;
            if (portalRef?.current?.contains(target)) {
              return;
            }
            if (sidenavPopoverPortalRef?.current?.contains(target)) {
              return;
            }
            setIsFocused(true);
            !isNavOpen && setIsNavOpen(true);
          }}
          onBlur={(e) => {
            const target = e?.target as HTMLElement;
            if (portalRef?.current?.contains(target)) {
              return;
            }
            if (sidenavPopoverPortalRef?.current?.contains(target)) {
              return;
            }
            setIsFocused(false);
            // This is required because shifting the focus from one nav element to another triggers onBlur and then immediately triggers onFocus handler. We want to await for 100 ms to make sure other nav elements are not focused before collapsing the nav
            debounce(
              () => !isFocusedReference.current && setIsNavOpen(false),
              100,
            )();
          }}
        >
          <NavItemsContainer>
            {shouldShowAdsAgentButton && (
              <ErrorBoundary name="AdsAgentNavButton">
                <AdsAgentNavButton isNavOpen={isNavOpen} />
              </ErrorBoundary>
            )}
            {toolbarItems
              .filter(
                (toolbarItem) =>
                  toolbarItem.sidebarType !== SIDEBAR_TYPE.DIVIDER &&
                  toolbarItem.id !== CC_MODAL_BULLSEYE_TOOLBAR_ID,
              )
              .map((toolbarItem) => {
                const isSingleApp =
                  toolbarItem.sidebarGroups.length == 1 &&
                  toolbarItem.sidebarGroups[0].links.length == 1;
                if (toolbarItem.sidebarType === SIDEBAR_TYPE.ALL_TOOLS) {
                  return (
                    <ErrorBoundary
                      key={toolbarItem.id}
                      name="SideNavAllToolsButton"
                    >
                      <SideNavAllToolsButton
                        toolbarItem={toolbarItem}
                        onMouseEnter={handleEnterTools}
                        onMouseLeave={() => handleLeaveTools(true)}
                      />
                    </ErrorBoundary>
                  );
                }
                if (isSingleApp) {
                  if (
                    toolbarItem.sidebarGroups[0].links[0].sidebarItemType ===
                    ('MODAL' as SideNavLinkItemType)
                  ) {
                    return (
                      <SideNavButton
                        key={toolbarItem.id}
                        id={toolbarItem.id}
                        allToolsEnabled={allToolsEnabled}
                      />
                    );
                  }
                  return (
                    <SideNavLink
                      key={toolbarItem.id}
                      id={toolbarItem.id}
                      isQuickNavModalEnabled={isQuickNavModalEnabled}
                      allToolsEnabled={allToolsEnabled}
                    />
                  );
                }
                return (
                  <Accordion
                    key={toolbarItem.id}
                    id={toolbarItem.id}
                    allToolsEnabled={allToolsEnabled}
                  />
                );
              })}
          </NavItemsContainer>
        </ToolbarWrapper>
        {!!allToolbarItems?.length && (
          <ErrorBoundary name="AllToolsHiddenContent">
            <AllToolsHiddenContent
              ref={containerRef}
              width={containerWidth}
              onMouseEnter={handleEnterTools}
              onMouseLeave={() => handleLeaveTools(false)}
              onFocus={() => {
                setIsFocused(true);
                !isAllToolsOpen && setIsAllToolsOpen(true);
              }}
              onBlur={() => {
                setIsFocused(false);
                debounce(
                  () => !isFocusedReference.current && setIsAllToolsOpen(false),
                  100,
                )();
              }}
            >
              {allToolbarItems?.map((item, index) => (
                <AllToolsNavItem
                  toolbarItem={item}
                  key={index}
                  data-takt-id={index}
                />
              ))}
            </AllToolsHiddenContent>
          </ErrorBoundary>
        )}
      </ToolbarOutterWrapper>
      {isNavOpen && <OverlayContainer />}
      <SideNavModal />
      {hasInteracted && showInterstitialModal && (
        <InterstitialErrorBoundary entityId={config.entityId}>
          <Suspense fallback={null}>
            <InterstitialModal
              isOpen={!!interstitialDestinationUrl}
              targetApp={interstitialAppName}
              destinationUrl={interstitialDestinationUrl}
              entityId={config.entityId}
              marketplaceId={config.marketplaceId}
              locale={config.locale}
              isMobile={false}
              modalElementId={'AACChromePortal'}
              dataMode={interstitialDataMode}
              onClose={handleClose}
            />
          </Suspense>
        </InterstitialErrorBoundary>
      )}
    </>
  );
};
